import { Info } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

interface ParsedSegment {
  text?: string
  range?: string
  verdict?: string
  tone: "good" | "warn" | "bad" | "neutral"
}

function classifyVerdict(verdict: string): ParsedSegment["tone"] {
  const v = verdict.toLowerCase()
  if (
    v.includes("refus") ||
    v.includes("sous-grade") ||
    v.includes("sub-grade")
  ) {
    return "bad"
  }
  if (v.includes("kg/") || v.includes("g2")) return "warn"
  if (v.includes("clean") || v.includes("conforme") || v.startsWith("g1")) {
    return "good"
  }
  return "neutral"
}

function parseRule(rule: string): ParsedSegment[] {
  const parts = rule.split("·").map((s) => s.trim()).filter(Boolean)
  return parts.map((part) => {
    const match = part.match(/^(\S+)\s+(.+)$/)
    if (!match) return { text: part, tone: "neutral" }
    const [, range, verdict] = match
    return { range, verdict, tone: classifyVerdict(verdict) }
  })
}

const TONE_DOT: Record<ParsedSegment["tone"], string> = {
  good: "bg-emerald-500",
  warn: "bg-amber-500",
  bad: "bg-red-500",
  neutral: "bg-muted-foreground/40",
}

interface LabFieldHintProps {
  fieldKey: string
}

export function LabFieldHint({ fieldKey }: LabFieldHintProps) {
  const { t } = useTranslation("intake")
  const rule = t(`lab.rules.${fieldKey}`, { defaultValue: "" })
  if (!rule) return null

  const segments = parseRule(rule)
  const hasStructure = segments.some((s) => s.range)

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`${t("lab.rulesHeading")}: ${rule}`}
          className="text-muted-foreground hover:text-foreground focus-visible:text-foreground inline-flex size-3.5 items-center justify-center rounded-full transition-colors focus:outline-none"
        >
          <Info className="size-3" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" align="end" sideOffset={4} className="max-w-[260px] p-2.5">
        <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-wide opacity-70">
          {t("lab.rulesHeading")}
        </div>
        {hasStructure ? (
          <div className="space-y-1">
            {segments.map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px]">
                {s.range ? (
                  <>
                    <span className="min-w-[52px] font-medium tabular-nums">
                      {s.range}
                    </span>
                    <span className={cn("size-1.5 shrink-0 rounded-full", TONE_DOT[s.tone])} />
                    <span className="opacity-80">{s.verdict}</span>
                  </>
                ) : (
                  <span className="opacity-80">{s.text}</span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-[11px] opacity-80">{rule}</div>
        )}
      </TooltipContent>
    </Tooltip>
  )
}
